import { getTheme } from '../theme/appTheme';

export const SMC_THEMES = {

  dark: {
    id: 'dark',
    name: 'Dark',
    label: 'Deep Space',
    type: 'dark',
    vars: {
      '--smc-bg':        '#0a0f1e',
      '--smc-panel':     '#0d1526',
      '--smc-el':        '#111d36',
      '--smc-deep':      '#060b18',
      '--smc-hov':       'rgba(255,255,255,0.04)',

      '--smc-ac':        '#0e9cc0',
      '--smc-ac2':       '#22e3c4',
      '--smc-space':     '#05070f',
      '--smc-ac-bg':     'rgba(14,156,192,0.10)',
      '--smc-ac-border': 'rgba(14,156,192,0.28)',

      '--smc-tx':        '#e8edf5',
      '--smc-tx2':       '#8892a4',
      '--smc-tx3':       '#4d5a6e',

      '--smc-bd':        'rgba(255,255,255,0.07)',
      '--smc-bd2':       'rgba(14,156,192,0.20)',

      '--smc-gn':        '#1D9E75',
      '--smc-gn-bg':     'rgba(29,158,117,0.12)',
      '--smc-am':        '#BA7517',
      '--smc-am-bg':     'rgba(186,117,23,0.12)',
      '--smc-rd':        '#E24B4A',
      '--smc-rd-bg':     'rgba(226,75,74,0.12)',
      '--smc-pu':        '#7F77DD',
      '--smc-pu-bg':     'rgba(127,119,221,0.12)',

      '--smc-shadow':    '0 4px 20px rgba(0,0,0,0.35)',
      '--smc-shadow-sm': '0 2px 8px rgba(0,0,0,0.25)',
    }
  },

  light: {
    id: 'light',
    name: 'Light',
    label: 'Solar',
    type: 'light',
    vars: {
      '--smc-bg':        '#f0f2f6',
      '--smc-panel':     '#ffffff',
      '--smc-el':        '#f8f9fb',
      '--smc-deep':      '#e8eaef',
      '--smc-hov':       'rgba(0,0,0,0.03)',

      '--smc-ac':        '#0284c7',
      '--smc-ac2':       '#0d9488',
      '--smc-space':     '#05070f',
      '--smc-ac-bg':     'rgba(2,132,199,0.08)',
      '--smc-ac-border': 'rgba(2,132,199,0.25)',

      '--smc-tx':        '#0f172a',
      '--smc-tx2':       '#475569',
      '--smc-tx3':       '#94a3b8',

      '--smc-bd':        'rgba(0,0,0,0.09)',
      '--smc-bd2':       'rgba(2,132,199,0.20)',

      '--smc-gn':        '#059669',
      '--smc-gn-bg':     'rgba(5,150,105,0.10)',
      '--smc-am':        '#d97706',
      '--smc-am-bg':     'rgba(217,119,6,0.10)',
      '--smc-rd':        '#dc2626',
      '--smc-rd-bg':     'rgba(220,38,38,0.10)',
      '--smc-pu':        '#7c3aed',
      '--smc-pu-bg':     'rgba(124,58,237,0.10)',

      '--smc-shadow':    '0 4px 20px rgba(0,0,0,0.08)',
      '--smc-shadow-sm': '0 2px 8px rgba(0,0,0,0.06)',
    }
  },

  space: {
    id: 'space',
    name: 'Space',
    label: 'Interstellar',
    type: 'dark',
    vars: {
      '--smc-bg':        '#05070f',
      '--smc-panel':     '#0c0f1c',
      '--smc-el':        '#141829',
      '--smc-deep':      '#020408',
      '--smc-hov':       'rgba(129,140,248,0.05)',

      '--smc-ac':        '#818cf8',
      '--smc-ac2':       '#22e3c4',
      '--smc-space':     '#05070f',
      '--smc-ac-bg':     'rgba(129,140,248,0.10)',
      '--smc-ac-border': 'rgba(129,140,248,0.28)',

      '--smc-tx':        '#f1f5ff',
      '--smc-tx2':       '#8b92b8',
      '--smc-tx3':       '#454d6d',

      '--smc-bd':        'rgba(129,140,248,0.10)',
      '--smc-bd2':       'rgba(129,140,248,0.22)',

      '--smc-gn':        '#34d399',
      '--smc-gn-bg':     'rgba(52,211,153,0.12)',
      '--smc-am':        '#fbbf24',
      '--smc-am-bg':     'rgba(251,191,36,0.12)',
      '--smc-rd':        '#f87171',
      '--smc-rd-bg':     'rgba(248,113,113,0.12)',
      '--smc-pu':        '#c084fc',
      '--smc-pu-bg':     'rgba(192,132,252,0.12)',

      '--smc-shadow':    '0 4px 20px rgba(0,0,0,0.50)',
      '--smc-shadow-sm': '0 2px 8px rgba(0,0,0,0.40)',
    }
  }
};

export const DEFAULT_SMC_THEME = 'dark';

// Swatch preview colors per theme [bg, accent, green]
export const THEME_SWATCHES = {
  dark:  ['#0a0f1e', '#0e9cc0', '#1D9E75'],
  light: ['#f0f2f6', '#0284c7', '#059669'],
  space: ['#05070f', '#818cf8', '#34d399'],
};

export const THEME_ICONS = { dark: '◐', light: '○', space: '✦' };

/**
 * Blend a `var(--smc-x,#hex)` token with transparency. `alphaHex` is the same
 * 2-digit hex-alpha suffix used to appear directly on raw hex colors (e.g. '44'
 * for ~27%) — kept for drop-in compatibility at call sites that migrated from
 * raw hex tokens to CSS-var tokens. Appending that suffix directly to a
 * `var(...)` string produces an invalid color (browsers silently drop the
 * whole declaration), so this goes through `color-mix()` instead.
 */
export function withAlpha(cssVar, alphaHex) {
  const pct = Math.round((parseInt(alphaHex, 16) / 255) * 100);
  return `color-mix(in srgb, ${cssVar} ${pct}%, transparent)`;
}

/** Map legacy appTheme tokens → --smc-* CSS vars when theme id is not dark/light/space */
function varsFromAppTheme(t) {
  if (!t) return null;
  const isDark = !!t.dark;
  const accent = t.accent || '#0e9cc0';
  const accentSoft = t.accentSoft || (isDark ? 'rgba(14,156,192,0.10)' : 'rgba(2,132,199,0.08)');
  return {
    '--smc-bg':        t.bg,
    '--smc-panel':     t.panel,
    '--smc-el':        t.cardBg || t.panel,
    '--smc-deep':      t.eqBg || t.bg,
    '--smc-hov':       isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    '--smc-ac':        accent,
    '--smc-ac-bg':     accentSoft,
    '--smc-ac-border': isDark ? `${accent}47` : `${accent}40`,
    '--smc-tx':        t.text,
    '--smc-tx2':       t.muted,
    '--smc-tx3':       isDark ? '#4d5a6e' : '#94a3b8',
    '--smc-bd':        t.border,
    '--smc-bd2':       isDark ? `${accent}33` : `${accent}33`,
    '--smc-gn':        isDark ? '#1D9E75' : '#059669',
    '--smc-gn-bg':     isDark ? 'rgba(29,158,117,0.12)' : 'rgba(5,150,105,0.10)',
    '--smc-am':        isDark ? '#BA7517' : '#d97706',
    '--smc-am-bg':     isDark ? 'rgba(186,117,23,0.12)' : 'rgba(217,119,6,0.10)',
    '--smc-rd':        isDark ? '#E24B4A' : '#dc2626',
    '--smc-rd-bg':     isDark ? 'rgba(226,75,74,0.12)' : 'rgba(220,38,38,0.10)',
    '--smc-pu':        isDark ? '#7F77DD' : '#7c3aed',
    '--smc-pu-bg':     isDark ? 'rgba(127,119,221,0.12)' : 'rgba(124,58,237,0.10)',
    '--smc-shadow':    isDark ? '0 4px 20px rgba(0,0,0,0.35)' : '0 4px 20px rgba(0,0,0,0.08)',
    '--smc-shadow-sm': isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.06)',
  };
}

export function applyThemeCSSVars(themeId) {
  const smcTheme = SMC_THEMES[themeId];
  let vars = smcTheme?.vars;
  if (!vars) {
    vars = varsFromAppTheme(getTheme(themeId)) || SMC_THEMES[DEFAULT_SMC_THEME].vars;
  }
  if (!vars) return;
  const root = document.documentElement;
  document.body.style.transition = 'background 0.25s ease, color 0.25s ease';
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute('data-smc-theme', themeId);
  const appTheme = getTheme(themeId);
  root.setAttribute('data-smc-type', smcTheme?.type || (appTheme?.dark ? 'dark' : 'light'));
}
